import PaymentMethod, {
  PaymentMethodType,
} from "@/models/payment-method/PaymentMethod";
import CardPaymentMethod from "@/models/payment-method/CardPaymentMethod";
import SepaPaymentMethod from "@/models/payment-method/SepaPaymentMethod";
import SubscriptionEntry from "@/models/subscription/SubscriptionEntry";
import camelcaseKeys from "camelcase-keys";
import { Module, Mutation, Action, VuexModule } from "vuex-module-decorators";
import Vue from "vue";
import { Emit } from "@/api/apiHelper";
import { showErrorSnackbar } from "@/utils/snackbarUtils";
import Invoice from "@/models/subscription/invoice";
import store from "../store";
import TradingBotStats from "@/models/TradingBotStats";
import ProfitabilityByBot from "@/models/subscription/profitabilityByBot";
import OBTPaymentMethod from "@/models/payment-method/OBTPaymentMethod";

@Module
export default class PaymentsModule extends VuexModule {
  // 📦 The payment method the current user is using
  public currentPaymentMethod: PaymentMethod | null = null;

  // 📦 The current user's subscription
  public currentSubscription: SubscriptionEntry | null = null;

  // 📦 All the invoices of the user's subscription
  public subscriptionInvoices: Invoice[] = [];

  // 📦 Profitability by bot that was part of that subscription period
  public profitabilityByBot: ProfitabilityByBot[] = [];

  // 📦 The approximate total profit made by the user's bot for that billing period
  public get totalProfitMade(): number {
    return this.currentSubscription?.currentPeriodProfit != null &&
      this.currentSubscription.currentPeriodProfit > 0
      ? this.currentSubscription.currentPeriodProfit
      : 0;
  }

  // 📦 The approximate total profit made by the user's bot for that billing period in EUR
  public get totalProfitMadeEuro(): number {
    return this.currentSubscription?.currentPeriodProfit != null &&
      this.currentSubscription.currentPeriodProfit > 0
      ? this.currentSubscription.currentPeriodProfit / this.conversionRate
      : 0;
  }

  // 📦 The amount due for the current user based on the current billing period
  public get currentAmountDue(): number {
    return this.currentSubscription?.currentAmountDue != null &&
      this.currentSubscription.currentAmountDue > 0
      ? this.currentSubscription.currentAmountDue
      : 0;
  }

  // 📦 The amount due in euro for the current user based on the current billing period
  public get currentAmountDueEuro(): number {
    return this.currentSubscription?.currentAmountDueEur != null &&
      this.currentSubscription.currentAmountDueEur > 0
      ? this.currentSubscription.currentAmountDueEur
      : 0;
  }

  // 📦 The conversion rate between EUR/USD
  public get conversionRate(): number {
    if (this.currentAmountDueEuro <= 0 || this.currentAmountDue <= 0) {
      return 1;
    } else {
      return this.currentAmountDue / this.currentAmountDueEuro;
    }
  }

  /**
   * 👾 Sets the current payment method of the user
   * @param paymentMethod
   */
  @Mutation
  private setCurrentPaymentMethod(paymentMethod: PaymentMethod) {
    this.currentPaymentMethod = paymentMethod;
  }

  /**
   * 👾 Sets the current subscription of the user
   * @param subscription
   */
  @Mutation
  private setCurrentSubscription(subscription: SubscriptionEntry) {
    this.currentSubscription = subscription;
  }

  /**
   * 👾 Sets the current subscription's invoices
   * @param invoices
   */
  @Mutation
  private setSubscriptionInvoices(invoices: Invoice[]) {
    this.subscriptionInvoices = invoices;
  }

  /**
   * 👾 Sets the current subscription's amount due by bot
   * @param amountDueByBot
   */
  @Mutation
  private setProfitabilityByBot(profitabilityByBot: ProfitabilityByBot[]) {
    this.profitabilityByBot = profitabilityByBot;
  }

  /**
   * 🚀 Fetches the current user's default payment method
   */
  @Action
  public async getCurrentPaymentMethod() {
    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/paymentmethods",
        null,
        Emit.None
      );
      let method;
      switch (response.data.method) {
        case PaymentMethodType.Card:
          method = new CardPaymentMethod(camelcaseKeys(response.data));
          break;
        case PaymentMethodType.Sepa:
          method = new SepaPaymentMethod(camelcaseKeys(response.data));
          break;
        default:
          method = new OBTPaymentMethod();
          break;
      }
      this.context.commit("setCurrentPaymentMethod", method);
    } catch (error) {
      this.context.commit("setCurrentPaymentMethod", null);
    }
  }

  /**
   * 🚀 Fetches the current user's Stripe subscription
   */
  @Action
  public async getCurrentSubscription() {
    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/paymentsubscriptions",
        null,
        Emit.None
      );
      this.context.commit(
        "setCurrentSubscription",
        new SubscriptionEntry(camelcaseKeys(response.data))
      );
    } catch (error) {
      this.context.commit("setCurrentSubscription", null);
    }
  }

  /**
   * 🚀 Fetches the profitability by bot for the current billing period
   */
  @Action
  public async getProfitabilityByBot() {
    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/paymentbots",
        null,
        Emit.None
      );
      const profitabilityByBot: ProfitabilityByBot[] = [];
      const bots: TradingBotStats[] = store.getters.getTradingBotStatsByIds(
        Object.keys(response.data)
      );
      bots.forEach((bot: TradingBotStats) => {
        const profitabilityBot = camelcaseKeys(response.data[bot.uid]);
        profitabilityByBot.push(
          new ProfitabilityByBot(
            profitabilityBot.currentPortfolio - profitabilityBot.startPortfolio,
            bot.uid,
            bot.strategy_name,
            bot.image,
            bot.exchange,
            bot.market,
            bot.candles,
            bot.enabled,
            bot.start_time,
            bot.stop_time
          )
        );
      });
      this.context.commit("setProfitabilityByBot", profitabilityByBot);
    } catch (error) {}
  }

  /**
   * 🚀 Fetches the current user's subscription invoices
   */
  @Action
  public async getSubscriptionInvoices() {
    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/invoices",
        null,
        Emit.None
      );
      const allInvoices: Invoice[] = camelcaseKeys(response.data).map(
        (rawData: any) =>
          new Invoice(
            rawData.amountDue,
            rawData.total,
            rawData.tax,
            rawData.paid,
            rawData.attempted,
            rawData.created,
            rawData.periodStart,
            rawData.periodEnd,
            rawData.paidTime,
            rawData.url,
            rawData.pdf
          )
      );
      this.context.commit(
        "setSubscriptionInvoices",
        allInvoices.filter((invoice: Invoice) => invoice.pdf != null)
      );
    } catch (error) {}
  }

  /**
   * 🚀 Sets / updates the payment method of the current user to the API
   * @param paymentMethods
   */
  @Action
  public async setPaymentMethod(type: string) {
    await Vue.prototype.$api.post("/api/v1/paymentmethods", {
      type: type,
    });
  }

  /**
   * 🚀 Removes the attached payment method
   */
  @Action
  public async deletePaymentMethod() {
    await Vue.prototype.$api.delete("/api/v1/paymentmethods");
    await this.context.dispatch("getCurrentPaymentMethod");
  }

  /**
   * 🚀 Posts the created payment method to the API
   */
  @Action
  private async createSubscription(subscriptionDetails: any) {
    await Vue.prototype.$api.post(
      "/api/v1/paymentsubscriptions",
      {
        subscription: subscriptionDetails.type,
        promo_code: subscriptionDetails.promotionalCode,
      },
      Emit.None
    );
  }

  /**
   * 🚀 Uncancels/Renews the current user's subscription plan
   */
  @Action
  public async continueCanceledSubscription() {
    await Vue.prototype.$api.put("/api/v1/paymentsubscriptions");
    await this.context.dispatch("getCurrentSubscription");
  }
}
